import { z } from "zod";
import { CatalogCardSchema } from "../catalog/catalog.js";
import { IdSchema, IsoDateTimeSchema, PersonNameSchema } from "../common/primitives.js";
import { ListingResultSchema } from "../search/search.js";
/**
 * Research: a listing an employee is considering buying, its condition check, the confirmed price
 * and (when needed) the admin's decision. Docs section 13.5 (M3) and decisions D-47 to D-51.
 */
/**
 * draft            — saved; condition and price being checked (editable);
 * waiting_approval — sent to the admin; not editable until withdrawn;
 * approved         — the admin said yes, up to a maximum price in USD;
 * rejected         — the admin said no, with a reason; can be revised and sent again (D-49);
 * closed           — no longer pursued (or bought, from M4); frees the listing for a new record.
 */
export const RESEARCH_STATUSES = ["draft", "waiting_approval", "approved", "rejected", "closed"];
export const ResearchStatusSchema = z.enum(RESEARCH_STATUSES);
export const CARD_TYPES = ["raw", "graded"];
export const CardTypeSchema = z.enum(CARD_TYPES);
/** Overall condition of a raw (ungraded) card, best first. */
export const RAW_CONDITIONS = [
    "near_mint",
    "lightly_played",
    "moderately_played",
    "heavily_played",
    "damaged",
];
export const RawConditionSchema = z.enum(RAW_CONDITIONS);
/** Answer to a single condition question, judged from the listing's photos. */
export const CHECK_ANSWERS = ["yes", "no", "cant_see"];
export const CheckAnswerSchema = z.enum(CHECK_ANSWERS);
export const GRADING_COMPANIES = ["psa", "bgs", "cgc", "sgc", "other"];
export const GradingCompanySchema = z.enum(GRADING_COMPANIES);
/** Every step in a record's history. */
export const RESEARCH_EVENT_TYPES = [
    "saved",
    "assessed",
    "price_confirmed",
    "submitted",
    "withdrawn",
    "approved",
    "rejected",
    "reopened",
    "closed",
    "deleted",
];
export const ResearchEventTypeSchema = z.enum(RESEARCH_EVENT_TYPES);
/** Where a confirmed price came from: eBay's live data, or typed by the employee from the website. */
export const PRICE_SOURCES = ["ebay", "employee"];
export const PriceSourceSchema = z.enum(PRICE_SOURCES);
export const ResearchIdSchema = z.uuid("Invalid research id");
export const ResearchPersonSchema = z.object({ id: IdSchema, name: PersonNameSchema });
/**
 * The listing as it was when saved. Kept unchanged when the listing changes later, so everyone sees what the
 * decision was based on. The match is checked again against the chosen card, using the listing's title.
 */
export const ResearchListingSchema = ListingResultSchema.omit({ id: true, foundAt: true }).extend({
    listingId: z.uuid(),
    /** When Pokifolio last saw the listing (its price and availability are from then). */
    seenAt: IsoDateTimeSchema,
});
/** Grades go from 1 to 10 in half steps (PSA 10, BGS 9.5, CGC 8.5). */
export const GradeSchema = z
    .number("Enter the grade as a number, e.g. 9 or 9.5")
    .min(1, "The grade must be between 1 and 10")
    .max(10, "The grade must be between 1 and 10")
    .refine((g) => Number.isInteger(g * 2), "Use whole or half grades, e.g. 9 or 9.5");
const NotesSchema = z.string().trim().max(2000, "Notes must be at most 2000 characters");
const CertificateSchema = z
    .string()
    .trim()
    .max(30, "The certificate number must be at most 30 characters")
    .regex(/^[A-Za-z0-9-]*$/, "Use only letters, numbers and dashes");
/** Turns "" (or nothing) into null, so an empty box is stored as nothing. */
const optionalText = (schema) => schema.nullish().transform((v) => (v === undefined || v === null || v === "" ? null : v));
/**
 * The condition check, judged from the listing's photos. A raw card gets an overall condition and four
 * questions; a graded card gets the grading company, the grade and (optionally) the certificate number.
 */
const answer = z.enum(CHECK_ANSWERS, "Choose Yes, No or Can't see");
export const AssessmentInputSchema = z.discriminatedUnion("cardType", [
    z.object({
        cardType: z.literal("raw"),
        overall: z.enum(RAW_CONDITIONS, "Choose the overall condition"),
        scratches: answer,
        whitening: answer,
        bends: answer,
        surface: answer,
        notes: optionalText(NotesSchema),
    }),
    z.object({
        cardType: z.literal("graded"),
        gradingCompany: z.enum(GRADING_COMPANIES, "Choose the grading company"),
        grade: GradeSchema,
        certificateNumber: optionalText(CertificateSchema),
        notes: optionalText(NotesSchema),
    }),
]);
/** PUT /api/v1/research/:id/assessment — saves a new version of the condition check. */
export const SaveAssessmentRequestSchema = z.object({
    assessment: AssessmentInputSchema,
    /** The record's version when the form was opened; a newer version means someone else changed it meanwhile. */
    version: z.number().int().min(1),
});
/** The latest condition check. Earlier versions are kept, never overwritten. */
export const AssessmentSchema = z.object({
    version: z.number().int().min(1),
    cardType: CardTypeSchema,
    overall: RawConditionSchema.nullable(),
    scratches: CheckAnswerSchema.nullable(),
    whitening: CheckAnswerSchema.nullable(),
    bends: CheckAnswerSchema.nullable(),
    surface: CheckAnswerSchema.nullable(),
    gradingCompany: GradingCompanySchema.nullable(),
    grade: z.number().nullable(),
    certificateNumber: z.string().nullable(),
    notes: z.string().nullable(),
    checkedBy: ResearchPersonSchema,
    checkedAt: IsoDateTimeSchema,
});
/** A confirmed price counts as current for this long; after that it must be checked again (D-54). */
export const PRICE_FRESH_HOURS = 6;
/** Whether a price confirmed at `confirmedAt` still counts as current. */
export function isPriceFresh(confirmedAt, now = new Date()) {
    return now.getTime() - new Date(confirmedAt).getTime() < PRICE_FRESH_HOURS * 60 * 60 * 1000;
}
const typedAmount = (what) => z
    .string(`Enter the ${what}`)
    .trim()
    .regex(/^\d{1,9}(\.\d{1,2})?$/, `Enter the ${what} as a number, e.g. 12.50`);
const CurrencyCodeSchema = z
    .string("Choose the currency")
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, "Choose the currency");
/**
 * The price check. For an eBay listing Pokifolio asks eBay for the price itself; for any other website the
 * employee types the price shown there. Shipping (to us) is always typed, as eBay's data does not include it.
 */
export const PriceInputSchema = z.discriminatedUnion("source", [
    z.object({
        source: z.literal("ebay"),
        shippingAmount: typedAmount("shipping cost"),
    }),
    z.object({
        source: z.literal("employee"),
        amount: typedAmount("price").refine((v) => Number(v) > 0, "The price must be more than 0"),
        currency: CurrencyCodeSchema,
        shippingAmount: typedAmount("shipping cost"),
    }),
]);
/** PUT /api/v1/research/:id/price */
export const ConfirmPriceRequestSchema = z.object({
    price: PriceInputSchema,
    /** The record's version when the form was opened (see SaveAssessmentRequestSchema). */
    version: z.number().int().min(1),
});
const DecimalSchema = z.string().regex(/^\d{1,14}\.\d{2,4}$/);
/** The confirmed price, in the listing's currency and in US dollars at that day's rate (D-48). */
export const PriceConfirmationSchema = z.object({
    source: PriceSourceSchema,
    amount: DecimalSchema,
    shippingAmount: DecimalSchema,
    currency: z.string().regex(/^[A-Z]{3}$/),
    /** Price plus shipping in US dollars. */
    totalUsd: DecimalSchema,
    /** How many units of the currency one US dollar bought that day; null for prices in US dollars. */
    fxRate: z.string().nullable(),
    fxDate: z.iso.date().nullable(),
    confirmedAt: IsoDateTimeSchema,
    confirmedBy: ResearchPersonSchema,
});
/** A maximum price in US dollars, as typed: "30", "29.50". */
const UsdInputSchema = z
    .string("Enter the amount in US dollars")
    .trim()
    .regex(/^\d{1,9}(\.\d{1,2})?$/, "Enter the amount in US dollars, e.g. 30 or 29.50")
    .refine((v) => Number(v) > 0, "The amount must be more than 0");
const versionField = z.number().int().min(1);
/** POST /api/v1/research/:id/submit — ask the admin for permission to buy, up to a maximum in US dollars (D-48). */
export const SubmitResearchRequestSchema = z.object({
    version: versionField,
    maxPriceUsd: UsdInputSchema,
    /** Why this card is worth buying. */
    note: optionalText(NotesSchema),
});
/** POST /api/v1/research/:id/approve — the maximum may be lowered, never raised; it defaults to the amount asked. */
export const ApproveResearchRequestSchema = z.object({
    version: versionField,
    approvedMaxUsd: UsdInputSchema.optional(),
    note: optionalText(NotesSchema),
});
/** POST /api/v1/research/:id/reject — always with a reason, so the request can be revised (D-49). */
export const RejectResearchRequestSchema = z.object({
    version: versionField,
    reason: z
        .string("Say why the request is rejected")
        .trim()
        .min(1, "Say why the request is rejected")
        .max(2000, "The reason must be at most 2000 characters"),
});
/** POST /api/v1/research/:id/close — stop researching this listing (frees it for a new record). */
export const CloseResearchRequestSchema = z.object({
    version: versionField,
    reason: optionalText(NotesSchema),
});
/** POST /api/v1/research/:id/withdraw and /reopen — no details, only the version the person was looking at. */
export const ResearchStepRequestSchema = z.object({ version: versionField });
/** DELETE /api/v1/research/:id?version=3 — takes the record out of every list; its history is kept (D-58). */
export const DeleteResearchQuerySchema = z.object({ version: z.coerce.number().int().min(1) });
/** What can be done to many records at once from the Research page. */
export const RESEARCH_BULK_ACTIONS = ["close", "delete"];
export const ResearchBulkActionSchema = z.enum(RESEARCH_BULK_ACTIONS);
export const RESEARCH_BULK_MAX = 100;
/**
 * POST /api/v1/research/bulk — closes or deletes the chosen records. Each record is checked and changed on its
 * own, exactly as if done one by one, so one refusal does not stop the rest.
 */
export const ResearchBulkRequestSchema = z.object({
    action: ResearchBulkActionSchema,
    items: z
        .array(z.object({ id: z.uuid("Not a valid research id"), version: versionField }))
        .min(1, "Choose at least one record")
        .max(RESEARCH_BULK_MAX, `Choose at most ${String(RESEARCH_BULK_MAX)} records at a time`)
        .refine((items) => new Set(items.map((i) => i.id)).size === items.length, "A record is listed twice"),
    /** Why they are closed (close only). */
    reason: optionalText(NotesSchema),
});
export const ResearchBulkResultSchema = z.discriminatedUnion("ok", [
    z.object({ id: z.uuid(), ok: z.literal(true) }),
    z.object({ id: z.uuid(), ok: z.literal(false), message: z.string() }),
]);
export const ResearchBulkResponseSchema = z.object({
    action: ResearchBulkActionSchema,
    results: z.array(ResearchBulkResultSchema),
});
/** The latest request to the admin (kept after the decision, so everyone sees what was asked). */
export const PurchaseRequestSchema = z.object({
    maxPriceUsd: DecimalSchema,
    note: z.string().nullable(),
    submittedAt: IsoDateTimeSchema,
});
export const DECISION_OUTCOMES = ["approved", "rejected"];
/** The admin's decision on the latest request. Earlier decisions stay in the history. */
export const DecisionSchema = z.object({
    outcome: z.enum(DECISION_OUTCOMES),
    /** The most the buyer may pay, in US dollars; null when rejected. */
    approvedMaxUsd: DecimalSchema.nullable(),
    /** The admin's note, or the reason for a rejection. */
    note: z.string().nullable(),
    decidedAt: IsoDateTimeSchema,
    decidedBy: ResearchPersonSchema,
});
export const ResearchRecordSchema = z.object({
    id: ResearchIdSchema,
    status: ResearchStatusSchema,
    /** The exact card (D-50). */
    card: CatalogCardSchema,
    listing: ResearchListingSchema,
    /** Who saved it and is responsible for it. */
    owner: ResearchPersonSchema,
    /** The latest condition check, or null when not done yet. */
    assessment: AssessmentSchema.nullable(),
    /** The confirmed price, or null when not checked yet. */
    price: PriceConfirmationSchema.nullable(),
    /** The latest request to the admin, or null when never sent. */
    request: PurchaseRequestSchema.nullable(),
    /** The admin's decision on that request, or null while waiting (or never sent). */
    decision: DecisionSchema.nullable(),
    /** Why the research was closed, when it was closed with a reason. */
    closedReason: z.string().nullable(),
    savedAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    /** Sent back with changes so two people cannot overwrite each other's work. */
    version: z.number().int().min(1),
});
/** POST /api/v1/research — save a listing from the search results, as the exact card it is (D-50). */
export const SaveResearchRequestSchema = z.object({
    listingId: z.uuid("Choose a listing from the search results"),
    cardId: z.string().trim().min(1, "Choose the exact card").max(64),
});
/**
 * `created` is false when the listing was already in Research (saved by you or a colleague): that record is
 * returned instead of a second one (one open record per listing).
 */
export const SaveResearchResponseSchema = z.object({
    research: ResearchRecordSchema,
    created: z.boolean(),
});
/** One step in a record's history: who did what, and when. */
export const ResearchEventSchema = z.object({
    id: z.number().int(),
    type: ResearchEventTypeSchema,
    actor: ResearchPersonSchema,
    at: IsoDateTimeSchema,
    /** The amount asked (sent) or allowed (approved), in US dollars; otherwise null. */
    maxPriceUsd: DecimalSchema.nullable(),
    /** The note or reason given with this step, if any. */
    note: z.string().nullable(),
});
/** GET /api/v1/research/:id — the record and its full history, oldest step first. */
export const ResearchResponseSchema = z.object({
    research: ResearchRecordSchema,
    history: z.array(ResearchEventSchema),
});
/** Whose research to list. Everyone in the team can see all research (D-52). */
export const RESEARCH_SCOPES = ["all", "mine"];
export const ResearchScopeSchema = z.enum(RESEARCH_SCOPES);
/** "open" = everything not closed (the default); "all" includes closed records. */
export const RESEARCH_STATUS_FILTERS = ["open", ...RESEARCH_STATUSES, "all"];
export const ResearchStatusFilterSchema = z.enum(RESEARCH_STATUS_FILTERS);
export const RESEARCH_PAGE_SIZE = 25;
/** What the Research list can be sorted by. */
export const RESEARCH_SORTS = ["updated", "created", "card", "price", "status"];
export const ResearchSortSchema = z.enum(RESEARCH_SORTS);
export const SORT_DIRECTIONS = ["asc", "desc"];
export const SortDirectionSchema = z.enum(SORT_DIRECTIONS);
/**
 * GET /api/v1/research?scope=mine&status=draft&q=charizard&sort=price&dir=asc&page=2
 * Most recently changed first unless sorted otherwise. `q` finds words in the card name, number, set or listing title.
 */
export const ResearchListQuerySchema = z.object({
    scope: ResearchScopeSchema.default("all"),
    status: ResearchStatusFilterSchema.default("open"),
    q: z
        .string()
        .trim()
        .max(100, "Search is too long")
        .optional()
        .transform((v) => (v === "" ? undefined : v)),
    sort: ResearchSortSchema.default("updated"),
    dir: SortDirectionSchema.default("desc"),
    page: z.coerce.number().int().min(1).max(10_000).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(RESEARCH_PAGE_SIZE),
});
export const ResearchListResponseSchema = z.object({
    data: z.array(ResearchRecordSchema),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    /** How many records match the filters, across all pages. */
    total: z.number().int().min(0),
});
const countField = z.number().int().min(0);
/** GET /api/v1/research/summary — counts for the dashboard and the Approvals menu item. */
export const ResearchSummarySchema = z.object({
    /** My research by step. */
    mine: z.object({
        draft: countField,
        waiting_approval: countField,
        approved: countField,
        rejected: countField,
        closed: countField,
    }),
    /** Everyone's requests waiting for the admin's decision. */
    waitingApproval: countField,
});
/** At most this many listings per "which of these are in Research?" question (a search returns up to 70). */
export const MAX_RESEARCH_LOOKUP = 100;
/** GET /api/v1/research/by-listing?ids=a,b,c — which of these listings are already in Research. */
export const ResearchByListingQuerySchema = z.object({
    ids: z
        .string()
        .transform((ids) => [...new Set(ids.split(",").map((id) => id.trim()))].filter((id) => id.length > 0))
        .pipe(z
        .array(z.uuid("Invalid listing id"))
        .min(1, "Give at least one listing id")
        .max(MAX_RESEARCH_LOOKUP, `Ask about at most ${String(MAX_RESEARCH_LOOKUP)} listings at a time`)),
});
/** A listing's open research record, as shown next to a search result ("In Research · Draft · Ayesha"). */
export const ListingResearchSchema = z.object({
    listingId: z.uuid(),
    researchId: z.uuid(),
    status: ResearchStatusSchema,
    owner: ResearchPersonSchema,
    card: CatalogCardSchema.pick({ id: true, name: true, number: true }),
});
export const ListingResearchResponseSchema = z.object({ data: z.array(ListingResearchSchema) });
//# sourceMappingURL=research.js.map